import {
  Avatar,
  Box,
  Button,
  Flex,
  Image,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Group,
  Paper,
  Badge,
  Container,
  Divider,
  Card,
  ActionIcon,
  ScrollArea,
  Chip,
  SegmentedControl,
  ThemeIcon,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import {
  IconCheck,
  IconMap,
  IconPlayerTrackNext,
  IconCurrencyDollar,
  IconRuler,
  IconLocation,
  IconClock,
  IconMapPin,
  IconChevronDown,
  IconChevronUp,
  IconCalendar,
  IconCircleCheck,
  IconProgress,
  IconAlertCircle,
  IconX,
  IconCamera,
  IconCar,
  IconExchange,
  IconUser,
  IconWheelchair
} from "@tabler/icons-react";
import axios from "axios";
import React, { memo, useEffect, useState } from "react";
import { useGeolocated } from "react-geolocated";
import { API_URL } from "../../services/url";
import { humenReadableTime } from "../../utils/datetime";
import { JobInfoProps, JobProps, TripProps } from "../CreateJob/type";
import { CameraAction } from "./cameraaction";

type Props = {
  jobId: string | null;
  tripInfoArray: TripProps[] | null | undefined;
  onRefetch: () => void;
  jobInfo: JobProps;
  onReassignOccurrence: (occurrence: JobInfoProps) => void;
};

// Define location return type to fix TypeScript errors
interface LocationData {
  latitude: number;
  longitude: number;
}

export const TripDetails: React.FC<Props> = memo(
  ({ jobId, tripInfoArray, onRefetch, jobInfo, onReassignOccurrence }) => {

    const userId = localStorage.getItem('userId')
    const [currentTrip, setCurrentTrip] = useState<TripProps[]>([])
    const [occurences, setOccurences] = useState<JobInfoProps[]>([])
    const [loading, setLoading] = useState(false)
    const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
    const [selectedTrip, setSelectedTrip] = useState<TripProps | null>(null);
    const [opened, { open, close }] = useDisclosure(false);
    const [jobImageURL, setJobImageURL] = useState<string>("");
    const [updating, setUpdating] = useState(false)
    const [imageModalOpened, imageModalHandlers] = useDisclosure(false);
    const [filterStatus, setFilterStatus] = useState('all')
    const [showCompletedJob, setShowCompletedJob] = useState(false)
    const [selectedJob, setSelectedJob] = useState<TripProps | null>(null)
    const [updateInvoiceModalOpened, updateInvoiceModalHandlers] = useDisclosure(false);
    const [selectedTripForUpdate, setSelectedTripForUpdate] = useState<TripProps | null>(null);
    const [updatingInvoice, setUpdatingInvoice] = useState(false);

    const isUser = localStorage.getItem("user") ? true : false;

    // repeat booking states
    const [hasActiveTrip, setHasActiveTrip] = useState(false)
    const [showAllOccurrences, setShowAllOccurrences] = useState(false);

    const style = { width: rem(16), height: rem(16) };
    const CheckIcon = <IconCheck style={style} color="green" />;
    const CloseIcon = <IconX style={style} color="red" />;
    const PendingIcon = <IconClock style={style} />;

    const getStatusBadge = (status: string) => {
      switch (status) {
        case "completed":
          return (
            <Badge color="green" size="sm" leftSection={CheckIcon}>
              Completed
            </Badge>
          );
        case "cancelled":
          return (
            <Badge color="red" size="sm" leftSection={CloseIcon}>
              Cancelled
            </Badge>
          );
        case "assigned":
          return (
            <Badge color="blue" size="sm" leftSection={CheckIcon}>
              Assigned
            </Badge>
          );
        case "in_progress":
          return (
            <Badge color="orange" size="sm" leftSection={PendingIcon}>
              In Progress
            </Badge>
          );
        default:
          return (
            <Badge color="gray" size="sm" leftSection={PendingIcon}>
              Pending
            </Badge>
          );
      }
    };

    const canReassignOccurrence = (occurrence: JobInfoProps) => {
      return (
        occurrence.status === "assigned" &&
        occurrence.assigned_to === userId &&
        occurrence.pickup_at.split('T')[0] !== new Date().toISOString().split('T')[0] &&
        !isUser
      );
    };

    const completedTrips = tripInfoArray?.filter((trip) => trip.status === 'completed');

    // repeat booking functions
    const filterOccurrences = (occurrence: JobInfoProps) => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'completed') return occurrence.status === 'completed';
      if (filterStatus === 'scheduled') return occurrence.status !== 'completed';
      return false;
    };

    // Geolocation setup
    const {
      coords,
      getPosition,
      isGeolocationAvailable,
      isGeolocationEnabled,
    } = useGeolocated({
      positionOptions: {
        enableHighAccuracy: true,  // Changed from false to true
        timeout: 10000,            // Added timeout
        maximumAge: 0              // Don't use cached positions
      },
      userDecisionTimeout: 5000,
      watchLocationPermissionChange: true,
      watchPosition: true,         // Continuously watch position for better accuracy
    });

    useEffect(() => {
      const activeTrips = tripInfoArray?.filter((trip) => trip.status === 'started' || trip.status === 'in_progress');
      setCurrentTrip(activeTrips || []);

      // Also check if any occurrence is in progress
      const hasActiveOccurrence = occurences.some(occ => occ.status === 'in_progress');
      setHasActiveTrip((activeTrips?.length ? true : false) || hasActiveOccurrence);
    }, [tripInfoArray, occurences]);

    useEffect(() => {
      const { occurences, ...jobInfoWithoutOccurences } = jobInfo;
      const mainJob = jobInfoWithoutOccurences as any;
      const allOccurences: JobInfoProps[] = [...occurences, mainJob];
      setOccurences(allOccurences);
    }, [jobInfo]);

    const handleAmount = (amount: string) => {
      const regex = /^\d*\.?\d{0,2}$/;

      if (regex.test(amount)) {
        selectedTrip && setSelectedTrip({
          ...selectedTrip,
          amount_paid: parseFloat(amount)
        });
      }
    };

    const handleDistance = (distance: string) => {
      selectedTrip && setSelectedTrip({
        ...selectedTrip,
        distance_driven: parseFloat(distance)
      });
    };

    const confirmStartTrip = (id?: string) => {
      modals.openConfirmModal({
        title: "Start Trip?",
        centered: true,
        children: <Text size="sm">Are you sure you want to start this trip?</Text>,
        labels: { confirm: "Start Trip", cancel: "Cancel" },
        confirmProps: { color: "dark" },
        onCancel: () => console.log("Trip start cancelled"),
        onConfirm: () => handleStartTrip(id && id),
      });
    };

    const handleStartTrip = async (id?: string) => {
      try {
        setLoading(true);

        const loc = await getLocation();
        if (!loc) {
          setLoading(false);
          return;
        }

        // Use regular trip start endpoint
        const res = await axios.post(`${API_URL}start-trip/`, {
          job_id: id ? id : jobId,
          driver_id: userId,
          start_location: (loc.latitude === 0 && loc.longitude === 0) ? null : `${loc.latitude},${loc.longitude}`,
          start_time: new Date().toISOString(),
          location_accuracy: (loc.latitude === 0 && loc.longitude === 0) ? null : locationAccuracy
        });

        if (res.data && res.data.id) {
          setLoading(false);
          showNotification({
            title: "Success",
            message: (loc.latitude === 0 && loc.longitude === 0) ?
              "Trip started successfully (without location tracking)" :
              locationAccuracy ?
                `Trip started successfully (location accuracy: ±${Math.round(locationAccuracy)}m)` :
                "Trip started successfully",
            color: "green",
          });
          onRefetch();
        }

      } catch (error) {
        setLoading(false);
        showNotification({
          title: "Error",
          message: "Failed to start trip",
          color: "red",
        });
      }
    };

    const getLocation = async (): Promise<LocationData | null> => {
      // Clear previous location accuracy
      setLocationAccuracy(null);

      console.log("Getting location - Current state:", {
        isGeolocationAvailable,
        isGeolocationEnabled,
        coords: coords ? { lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy } : null
      });

      // Check if geolocation is available in the browser
      if (!isGeolocationAvailable) {
        showNotification({
          title: "Location Not Supported",
          message: "Your browser doesn't support location services",
          color: "red",
        });
        return null;
      }

      // If location is not enabled or coords are null, check permission state
      if (!isGeolocationEnabled || !coords) {
        // First try to get position to refresh the state
        getPosition();
        await new Promise(resolve => setTimeout(resolve, 500));

        // Check again after attempting to get position
        if (!isGeolocationEnabled && !coords) {
          const userWantsToEnableLocation = await new Promise<boolean>(resolve => {
            modals.openConfirmModal({
              title: "Location Permission Required",
              centered: true,
              children: (
                <Stack gap="md">
                  <Text size="sm">
                    Location access is required to track trip start and end points.
                  </Text>
                  <Text size="xs" c="dimmed">
                    Your browser will ask for permission to access your location.
                    Please click "Allow" when prompted.
                  </Text>
                </Stack>
              ),
              labels: {
                confirm: "Enable Location",
                cancel: "Continue Without Location"
              },
              confirmProps: { color: "dark" },
              onCancel: () => resolve(false),
              onConfirm: () => resolve(true),
            });
          });

          if (!userWantsToEnableLocation) {
            // Allow user to proceed without location
            return await new Promise<LocationData | null>(resolve => {
              modals.openConfirmModal({
                title: "Continue Without Location?",
                centered: true,
                children: (
                  <Stack gap="md">
                    <Text size="sm">
                      Without location access, the trip start/end points won't be recorded accurately.
                    </Text>
                    <Text size="sm" fw={500}>
                      Do you want to continue anyway?
                    </Text>
                  </Stack>
                ),
                labels: {
                  confirm: "Continue Anyway",
                  cancel: "Go Back"
                },
                confirmProps: { color: "orange" },
                onCancel: () => resolve(null),
                onConfirm: () => {
                  // Return a default location or null coordinates
                  resolve({
                    latitude: 0,
                    longitude: 0
                  });
                },
              });
            });
          }

          // Try to get position after user agrees
          getPosition();

          // Wait a bit for the permission prompt
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Check again if location is enabled after permission request
          if (!coords) {
            showNotification({
              title: "Location Permission Denied",
              message: "Please enable location in your browser settings and try again",
              color: "orange",
            });

            // Still allow to proceed without location
            return await new Promise<LocationData | null>(resolve => {
              modals.openConfirmModal({
                title: "Proceed Without Location?",
                centered: true,
                children: (
                  <Text size="sm">
                    Location permission was denied. Do you want to continue without recording accurate location?
                  </Text>
                ),
                labels: {
                  confirm: "Continue",
                  cancel: "Cancel"
                },
                confirmProps: { color: "orange" },
                onCancel: () => resolve(null),
                onConfirm: () => resolve({
                  latitude: 0,
                  longitude: 0
                }),
              });
            });
          }
        }
      }

      // Get the best available location
      const accurateLocation = await getAccurateLocation();

      if (!accurateLocation) {
        // Try native API one more time as a last resort
        if (navigator.geolocation) {
          try {
            console.log("Attempting native geolocation as final fallback");
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(
                resolve,
                reject,
                {
                  enableHighAccuracy: false, // Try without high accuracy
                  timeout: 10000,
                  maximumAge: 5000 // Allow slightly cached position
                }
              );
            });

            const locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };

            setLocationAccuracy(locationData.accuracy);

            return locationData;
          } catch (err) {
            console.error("Final native geolocation attempt failed:", err);
          }
        }

        // If still no location after all attempts, allow manual continuation
        return await new Promise<LocationData | null>(resolve => {
          modals.openConfirmModal({
            title: "Location Not Available",
            centered: true,
            children: (
              <Stack gap="md">
                <Text size="sm">
                  Could not get your current location. This might be due to:
                </Text>
                <Stack gap="xs">
                  <Text size="xs">• Poor GPS/network signal</Text>
                  <Text size="xs">• Location services disabled</Text>
                  <Text size="xs">• Browser restrictions</Text>
                </Stack>
                <Text size="sm" fw={500}>
                  Continue without accurate location?
                </Text>
              </Stack>
            ),
            labels: {
              confirm: "Continue",
              cancel: "Try Again"
            },
            confirmProps: { color: "orange" },
            onCancel: () => {
              // Try again
              setTimeout(() => {
                getPosition();
              }, 100);
              resolve(null);
            },
            onConfirm: () => resolve({
              latitude: 0,
              longitude: 0
            }),
          });
        });
      }

      // Store the accuracy for display
      setLocationAccuracy(accurateLocation.accuracy);

      // Only proceed if accuracy is reasonable
      if (accurateLocation.accuracy > 1000) { // 1000 meters = 1km
        showNotification({
          title: "Poor Location Accuracy",
          message: `Current accuracy is ${Math.round(accurateLocation.accuracy)}m, which may be off by ~${Math.round(accurateLocation.accuracy / 1000)}km. Try again in an open area.`,
          color: "orange",
        });
        // Show a confirm dialog to let the user decide
        return await new Promise<LocationData | null>(resolve => {
          modals.openConfirmModal({
            title: "Low Location Accuracy",
            centered: true,
            children: (
              <Text size="sm">
                Your current location accuracy is poor (±{Math.round(accurateLocation.accuracy)}m).
                This may result in an incorrect location being recorded.
                Do you still want to proceed?
              </Text>
            ),
            labels: { confirm: "Proceed Anyway", cancel: "Try Again" },
            confirmProps: { color: "orange" },
            onCancel: () => resolve(null),
            onConfirm: () => resolve({
              latitude: accurateLocation.latitude,
              longitude: accurateLocation.longitude
            }),
          });
        });
      }

      return {
        latitude: accurateLocation.latitude,
        longitude: accurateLocation.longitude
      };
    };

    const getAccurateLocation = async (maxRetries = 3): Promise<{ latitude: number, longitude: number, accuracy: number } | null> => {
      let retries = 0;
      let bestLocation = null;

      // Force a fresh position reading before starting
      getPosition();

      while (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Wait longer between readings

        // Force position update on each retry
        getPosition();

        // Also try using the native API as a fallback
        if (!coords && navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
              });
            });

            console.log(`Native geolocation reading: Accuracy ${position.coords.accuracy}m`);
            bestLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };

            if (position.coords.accuracy < 50) {
              break;
            }
          } catch (err) {
            console.log("Native geolocation failed:", err);
          }
        } else if (coords && coords.accuracy) {
          console.log(`Location reading ${retries + 1}: Accuracy ${coords.accuracy}m`);

          // Keep the most accurate reading
          if (!bestLocation || (coords.accuracy < bestLocation.accuracy)) {
            bestLocation = {
              latitude: coords.latitude,
              longitude: coords.longitude,
              accuracy: coords.accuracy
            };
          }

          // If accuracy is already good enough, stop trying
          if (coords.accuracy < 50) { // 50 meters is quite accurate
            break;
          }
        }

        retries++;
      }

      return bestLocation;
    };

    const LocationButton = ({ latLongString }: { latLongString: string }) => {
      const handleRedirect = () => {
        const url = createGoogleMapsUrl(latLongString);
        window.location.href = url;
      };

      // Don't show button for invalid coordinates
      if (!latLongString || latLongString === "0,0") {
        return (
          <Text size="xs" c="dimmed" fs="italic">
            Location not available
          </Text>
        );
      }

      return (
        <Button
          onClick={handleRedirect}
          variant="outline"
          size="xs"
          leftSection={<IconMapPin size={16} />}
          color="gray"
        >
          View on Map
        </Button>
      );
    };

    const LocationVerificationMap = ({ latLong, accuracy }: { latLong: string, accuracy?: number }) => {
      const [lat, lng] = latLong.split(',').map(parseFloat);

      return (
        <Box>
          <Text size="sm" mb="xs">Your current location{accuracy ? ` (±${Math.round(accuracy)}m)` : ''}:</Text>
          <Paper
            p="xs"
            style={{
              backgroundColor: '#f0f0f0',
              border: '1px solid #ddd',
              borderRadius: '4px',
              marginBottom: '10px'
            }}
          >
            <Group justify="space-between">
              <Text size="xs">Lat: {lat.toFixed(6)}, Lng: {lng.toFixed(6)}</Text>
              <Button
                size="xs"
                variant="subtle"
                component="a"
                href={`https://www.google.com/maps?q=${lat},${lng}`}
                target="_blank"
                rightSection={<IconMap size={14} />}
              >
                Verify
              </Button>
            </Group>
          </Paper>
        </Box>
      );
    };

    const createGoogleMapsUrl = (latLongString: string) => {
      const [latitude, longitude] = latLongString.split(",");
      return `https://www.google.com/maps?q=${latitude},${longitude}`;
    };

    const handleComplete = async (tripId: string, hadTripInfo: boolean) => {
      try {
        const loc = await getLocation();
        if (!loc) return;

        if (hadTripInfo) {
          const foundTrip = tripInfoArray?.find((trip) => trip.id === tripId);

          if (foundTrip) {
            setSelectedTrip({
              ...foundTrip,
              amount_paid: 0,
              distance_driven: 0,
              end_location: (loc.latitude === 0 && loc.longitude === 0) ? "0,0" : `${loc.latitude},${loc.longitude}`,
            });
            open();
          }
        } else {
          try {
            const res = await axios.get(`${API_URL}trip-details/?job_id=${tripId}`);

            const foundTrip = res.data[0];

            if (res.data.length > 0) {
              setSelectedTrip({
                ...foundTrip,
                amount_paid: 0,
                distance_driven: 0,
                end_location: (loc.latitude === 0 && loc.longitude === 0) ? "0,0" : `${loc.latitude},${loc.longitude}`,
              });
              open();
            }

          } catch (error) {

          }
        }
      } catch (error) {
        showNotification({
          title: "Error",
          message: "Failed to complete trip",
          color: "red",
        });
      }
    };

    const handleJobImg = (file: File) => {
      if (jobImageURL) {
        // uploaded image delete function here
        console.log('dummy delete function');
      }
      uploadFile(file, setJobImageURL);
    }

    const generateRandomString = (length: number): string => {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      return result;
    };

    const handleUpdateInvoice = async (file: File) => {
      if (!selectedTripForUpdate) return;

      setUpdatingInvoice(true);

      try {
        // Upload the new image first
        const randomString = generateRandomString(8);
        const newFileName = `invoice_${selectedTripForUpdate.id}_${randomString}.${file.name.split('.')[1]}`;

        const arrayBuffer = await file.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        const uploadResponse = await axios.post(
          `https://s5ttwy64zmbm7hs3ddcmk56x6i0wrnla.lambda-url.ap-south-1.on.aws/?file_name=${encodeURIComponent(newFileName)}`,
          { file_data: base64 },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const newInvoiceUrl = uploadResponse.data.file_url;

        // Update the trip invoice via API
        const updateResponse = await axios.post(
          'https://api.kabbie.com.au/api/update-trip-invoice/',
          {
            trip_id: selectedTripForUpdate.id,
            invoice_url: newInvoiceUrl
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        showNotification({
          title: "Success",
          message: "Invoice updated successfully!",
          color: "green",
        });

        // Refresh the page to show updated data
        window.location.reload();

      } catch (error) {
        console.error('Error updating invoice:', error);
        showNotification({
          title: "Error",
          message: "Failed to update invoice. Please try again.",
          color: "red",
        });
      } finally {
        setUpdatingInvoice(false);
        updateInvoiceModalHandlers.close();
        setSelectedTripForUpdate(null);
      }
    };

    const uploadFile = async (file: File, urlStateUpdater: React.Dispatch<React.SetStateAction<string>>) => {
      if (!file) return;

      const randomString = generateRandomString(8);
      const newFileName = `${file.name.split('.')[0]}_${randomString}.${file.name.split('.')[1]}`;

      setUpdating(true);

      try {
        // Read the file as an ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();
        // Convert ArrayBuffer to Base64
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );

        const response = await axios.post(
          `https://s5ttwy64zmbm7hs3ddcmk56x6i0wrnla.lambda-url.ap-south-1.on.aws/?file_name=${encodeURIComponent(newFileName)}`,
          { file_data: base64 },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('File uploaded:', response.data);
        urlStateUpdater(response.data.file_url);
        setUpdating(false);
      } catch (error) {
        console.error('Error uploading file:', error);
        showNotification({
          title: "Error",
          message: "Failed to upload file. Please try again.",
          color: "red",
        });
        setUpdating(false);
      }
    };

    const handleSaveTrip = async () => {
      if (!selectedTrip) return;

      try {
        setLoading(true);

        const localEndTime = new Date();
        // Format time as YYYY-MM-DDTHH:mm:ss without milliseconds and timezone
        const endTimeFormatted = localEndTime.toISOString();

        // Use regular trip end endpoint
        const endTripData = {
          trip_id: selectedTrip.id,
          end_location: selectedTrip.end_location,
          end_time: endTimeFormatted,
          distance_driven: selectedTrip.distance_driven,
          amount_paid: selectedTrip.amount_paid.toFixed(2),
          invoiceurl: jobImageURL,
          client_timezone_offset: localEndTime.getTimezoneOffset(),
          location_accuracy: locationAccuracy
        };

        const res = await axios.post(`${API_URL}end-trip/`, endTripData);

        if (res.data && res.data.id) {
          setLoading(false);
          showNotification({
            title: "Success",
            message: "Trip completed successfully",
            color: "green",
          });
          onRefetch();
          close();
        }

      } catch (error) {
        setLoading(false);
        showNotification({
          title: "Error",
          message: "Failed to complete trip",
          color: "red",
        });
      }
    };

    const handleShowCompletedJob = async (occurrence: JobInfoProps) => {
      try {
        const res = await axios.get(`${API_URL}trip-details/?job_id=${occurrence.id}`);
        if (res.data.length > 0) {
          setSelectedJob(res.data[0])
          setShowCompletedJob(true)
        } else {
          showNotification({
            title: "Error",
            message: "There is no tripinfo",
            color: "red",
          });
        }
      } catch (error) {
        console.log("Failed to fetch completed job's trip info");
        showNotification({
          title: "Error",
          message: "Failed to show completed job",
          color: "red",
        });
      }
    }

    const isFormValid = selectedTrip?.amount_paid && selectedTrip?.distance_driven;

    return (
      <Container p={0} w={"100%"}>

        {/* Complete Trip Modal */}
        <Modal
          opened={opened}
          centered
          onClose={() => { close(); setSelectedTrip(null) }}
          title={<Text fw={700}>Complete Trip</Text>}
          size="md"
        >
          <Stack gap="md" my="md">
            <Box mb="sm">
              <CameraAction
                showTitle
                onCapture={handleJobImg}
                uploadingImage={updating}
              />
            </Box>

            {selectedTrip?.end_location && selectedTrip.end_location !== "0,0" && locationAccuracy && (
              <LocationVerificationMap
                latLong={selectedTrip.end_location}
                accuracy={locationAccuracy}
              />
            )}

            {(!selectedTrip?.end_location || selectedTrip.end_location === "0,0") && (
              <Paper p="xs" bg="orange.1" radius="md">
                <Group gap="xs">
                  <IconAlertCircle size={16} color="orange" />
                  <Text size="xs" c="orange.7">
                    Location not recorded - Trip proceeding without GPS tracking
                  </Text>
                </Group>
              </Paper>
            )}

            <TextInput
              label="Amount Collected ($)"
              placeholder="Example: 1200"
              required
              type="number"
              rightSection={<IconCurrencyDollar size={16} />}
              value={selectedTrip?.amount_paid || ''}
              onChange={(event) => handleAmount(event.currentTarget.value)}
            />

            <TextInput
              label="Distance Travelled (km)"
              placeholder="Example: 10"
              required
              type="number"
              rightSection={<IconRuler size={16} />}
              value={selectedTrip?.distance_driven || ''}
              onChange={(event) => handleDistance(event.currentTarget.value)}
            />

            <Flex justify="center" mt="md">
              <Button
                w="80%"
                variant="gradient"
                gradient={{ from: "#2B2B2B", to: "#404040", deg: 180 }}
                radius="md"
                disabled={!isFormValid || loading || updating}
                rightSection={!updating && <IconCheck size={18} />}
                onClick={handleSaveTrip}
                loading={loading}
                loaderProps={{ type: "oval" }}
              >
                {updating ? 'Uploading...' : 'Complete Trip'}
              </Button>
            </Flex>
          </Stack>
        </Modal>

        {/* Invoice Image Modal */}
        <Modal
          opened={imageModalOpened}
          centered
          onClose={() => {
            imageModalHandlers.close();
            setSelectedTrip(null);
          }}

          title={<Text fw={700}>Invoice</Text>}
          size="lg"
          zIndex={5}
        >
          <Box h="70vh" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto' }}>
            <Image src={selectedTrip?.invoiceurl} fit="contain" />
          </Box>
        </Modal>

        {/* completed job modal */}
        <Modal
          opened={showCompletedJob}
          centered
          onClose={() => {
            setShowCompletedJob(false);
            setSelectedJob(null);
          }}

          title={<Text fw={700}>Invoice</Text>}
          size="lg"
          zIndex={4}
        >
          {selectedJob && <Paper
            shadow="sm"
            p="md"
            radius="md"
            withBorder
          >
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500}>
                  Trip by {selectedJob.driver_name}
                </Text>
                <Group gap="sm">
                  <Badge color="green" variant="light">
                    Completed
                  </Badge>
                  <Badge color="dark" size="xl" variant="filled">
                    {jobInfo.booking_id}
                  </Badge>
                </Group>
              </Group>

              <Divider />

              <Group justify="space-between">
                <Text size="sm">Start</Text>
                <Text size="sm" c="dimmed">{humenReadableTime(selectedJob.start_time)}</Text>
              </Group>

              <Group justify="space-between">
                <Text size="sm">End</Text>
                <Text size="sm" c="dimmed">{humenReadableTime(selectedJob.end_time)}</Text>
              </Group>

              <Divider />

              <Group justify="space-between">
                <Text size="sm" fw={500}>Distance</Text>
                <Text size="sm" fw={700}>{`${selectedJob.distance_driven} km`}</Text>
              </Group>

              <Group justify="space-between">
                <Text size="sm" fw={500}>Amount</Text>
                <Text size="sm" fw={700}>{`$${(selectedJob.amount_paid).toFixed(2)}`}</Text>
              </Group>

              <Divider />

              <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>Invoice</Text>
                {selectedJob.invoiceurl ? (
                  <Group gap="sm">
                    <ActionIcon
                      variant="light"
                      color="gray"
                      onClick={() => {
                        setSelectedTrip(selectedJob);
                        imageModalHandlers.open();
                      }}
                    >
                      <Avatar
                        src={selectedJob.invoiceurl}
                        size="md"
                        radius="sm"
                      />
                    </ActionIcon>
                    {jobInfo.assigned_to === userId && <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => {
                        setSelectedTripForUpdate(selectedJob);
                        updateInvoiceModalHandlers.open();
                      }}
                    >
                      <IconCamera size={16} />
                    </ActionIcon>}
                  </Group>
                ) : (
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    leftSection={<IconCamera size={14} />}
                    onClick={() => {
                      setSelectedTripForUpdate(selectedJob);
                      updateInvoiceModalHandlers.open();
                    }}
                  >
                    Add Invoice
                  </Button>
                )}
              </Group>
            </Stack>
          </Paper>
          }
        </Modal>

        {jobInfo.repeat_booking && tripInfoArray ? <Stack gap="md">
          <SegmentedControl
            fullWidth
            value={filterStatus}
            onChange={setFilterStatus}
            data={[
              { label: `All (${occurences.length})`, value: "all" },
              {
                label: `Completed (${occurences.filter((o) => o.status === "completed").length
                  })`,
                value: "completed",
              },
              {
                label: `Scheduled (${occurences.filter((o) => o.status !== "completed").length
                  })`,
                value: "scheduled",
              },
            ]}
            color="dark"
            radius="md"
          />

          <Stack gap="lg">
            <Title order={3}>Upcoming Jobs</Title>
            {occurences
              .filter(filterOccurrences)
              .sort((a, b) => {
                return (
                  new Date(a.pickup_at).getTime() -
                  new Date(b.pickup_at).getTime()
                );
              })
              .slice(0, showAllOccurrences ? undefined : 3)
              .map((occurrence) => (
                <Paper
                  key={occurrence.id}
                  shadow="sm"
                  p="lg"
                  radius="md"
                  withBorder
                  onClick={() => {
                    if (occurrence.status === "completed") {
                      handleShowCompletedJob(occurrence);
                    }
                  }}
                >
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Badge color="dark" size="xl" variant="filled">
                        {occurrence.booking_id}
                      </Badge>
                      {getStatusBadge(occurrence.status)}
                    </Group>

                    <Divider />

                    <Group justify="space-between">
                      <Group gap="sm">
                        <ThemeIcon variant="light" color="gray">
                          <IconCalendar
                            style={{ width: rem(16), height: rem(16) }}
                          />
                        </ThemeIcon>
                        <Text size="sm">Date</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {new Date(occurrence.pickup_at).toLocaleDateString()}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Group gap="sm">
                        <ThemeIcon variant="light" color="gray">
                          <IconClock
                            style={{ width: rem(16), height: rem(16) }}
                          />
                        </ThemeIcon>
                        <Text size="sm">Time</Text>
                      </Group>
                      <Text size="sm" fw={500}>
                        {new Date(occurrence.pickup_at).toLocaleTimeString(
                          "en-US",
                          {
                            hour12: true,
                            hour: "numeric",
                            minute: "numeric",
                          }
                        )}
                      </Text>
                    </Group>

                    <Group justify="space-between">
                      <Group gap="sm">
                        <ThemeIcon
                          variant="light"
                          color={
                            occurrence.services_required.includes("wheel")
                              ? "blue"
                              : "gray"
                          }
                        >
                          {occurrence.services_required.includes("wheel") ? (
                            <IconWheelchair
                              style={{ width: rem(16), height: rem(16) }}
                            />
                          ) : (
                            <IconCar
                              style={{ width: rem(16), height: rem(16) }}
                            />
                          )}
                        </ThemeIcon>
                        <Text size="sm">Service</Text>
                      </Group>
                      <Text size="sm" fw={500} tt="capitalize">
                        {occurrence.services_required}
                      </Text>
                    </Group>

                    {occurrence.assigned_driver_name && (
                      <Group justify="space-between">
                        <Group gap="sm">
                          <ThemeIcon variant="light" color="gray">
                            <IconUser
                              style={{ width: rem(16), height: rem(16) }}
                            />
                          </ThemeIcon>
                          <Text size="sm">Assigned To</Text>
                        </Group>
                        <Text size="sm" fw={500}>
                          {occurrence.assigned_driver_name}
                        </Text>
                      </Group>
                    )}

                    {occurrence.status !== "completed" && (
                      <>
                        <Divider />
                        {canReassignOccurrence(occurrence) ? <Button
                          fullWidth
                          variant="light"
                          color="orange"
                          leftSection={<IconExchange size={14} />}
                          onClick={() => onReassignOccurrence(occurrence)}
                        >
                          Reassign
                        </Button> : occurrence.status === "assigned" ? <Button
                          fullWidth
                          variant="gradient"
                          gradient={{ from: "dark", to: "gray", deg: 145 }}
                          onClick={() => confirmStartTrip(occurrence.id)}
                        >
                          Start Trip
                        </Button> : <Button
                          fullWidth
                          variant="gradient"
                          gradient={{ from: "dark", to: "gray", deg: 145 }}
                          onClick={() => handleComplete(occurrence.id, false)}
                        >
                          Complete Trip
                        </Button>}
                      </>
                    )}
                  </Stack>
                </Paper>
              ))}
          </Stack>

          {/* Show More/Less Button */}
          {occurences.filter(filterOccurrences).length > 3 && (
            <Button
              variant="subtle"
              fullWidth
              onClick={() => setShowAllOccurrences(!showAllOccurrences)}
              rightSection={showAllOccurrences ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            >
              {showAllOccurrences ? 'Show Less' : `Show ${occurences.filter(filterOccurrences).length - 3} More`}
            </Button>
          )}
        </Stack> : !tripInfoArray ? <Box mt="xl" p="md">
          <Button
            fullWidth
            size="lg"
            variant="gradient"
            gradient={{ from: "dark", to: "gray", deg: 145 }}
            radius="md"
            disabled={loading}
            rightSection={<IconPlayerTrackNext size={20} />}
            onClick={() => confirmStartTrip()}
            loading={loading}
            loaderProps={{ type: "oval" }}
          >
            Start Trip
          </Button>
        </Box> : jobInfo.assigned_to === userId && <Box mb="xl">
          <Title order={3} mb="lg">
            Current Trip
          </Title>

          {currentTrip.map((tripInfo) => (
            <Paper key={tripInfo.id} shadow="sm" p="lg" radius="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between">
                  <Group gap="sm">
                    <Text size="sm" fw={500}>Status</Text>
                    <Badge color="blue" variant="light" tt="capitalize">
                      {tripInfo.status}
                    </Badge>
                  </Group>
                  <Badge color="dark" size="xl" variant="filled">
                    {jobInfo.booking_id}
                  </Badge>
                </Group>

                {tripInfo?.start_location && (
                  <Group justify="space-between">
                    <Text size="sm">Started From</Text>
                    <LocationButton latLongString={tripInfo.start_location} />
                  </Group>
                )}

                {tripInfo?.start_time && (
                  <Group justify="space-between">
                    <Text size="sm">Time</Text>
                    <Text size="sm" fw={500}>{humenReadableTime(tripInfo.start_time)}</Text>
                  </Group>
                )}

                {(tripInfo?.status === "started" || tripInfo?.status === "in_progress") && (
                  <>
                    <Divider />
                    <Button
                      fullWidth
                      variant="gradient"
                      gradient={{ from: "#2B2B2B", to: "#404040", deg: 180 }}
                      radius="md"
                      disabled={loading}
                      rightSection={<IconCheck size={18} />}
                      onClick={() => handleComplete(tripInfo.id, true)}
                      loading={loading}
                      loaderProps={{ type: "oval" }}
                    >
                      Complete Trip
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>
          ))}
        </Box>}

        {completedTrips && !jobInfo.repeat_booking && <Box mt="xl" >
          <Title order={3} mb="lg">Completed Trips</Title>

          <Stack gap="lg">
            {completedTrips?.map((tripInfo) => (
              <Paper
                key={tripInfo.id}
                shadow="sm"
                p="lg"
                radius="md"
                withBorder
              >
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={500}>
                      Trip by {tripInfo.driver_name}
                    </Text>
                    <Group gap="sm">
                      <Badge color="green" variant="light">
                        Completed
                      </Badge>
                      <Badge color="dark" size="xl" variant="filled">
                        {jobInfo.booking_id}
                      </Badge>
                    </Group>
                  </Group>

                  <Divider />

                  <Group justify="space-between">
                    <Text size="sm">Start</Text>
                    <Text size="sm" c="dimmed">{humenReadableTime(tripInfo.start_time)}</Text>
                  </Group>

                  <Group justify="space-between">
                    <Text size="sm">End</Text>
                    <Text size="sm" c="dimmed">{humenReadableTime(tripInfo.end_time)}</Text>
                  </Group>

                  <Divider />

                  <Group justify="space-between">
                    <Text size="sm" fw={500}>Distance</Text>
                    <Text size="sm" fw={700}>{`${tripInfo.distance_driven} km`}</Text>
                  </Group>

                  <Group justify="space-between">
                    <Text size="sm" fw={500}>Amount</Text>
                    <Text size="sm" fw={700}>{`$${(tripInfo.amount_paid).toFixed(2)}`}</Text>
                  </Group>

                  <Divider />

                  <Group justify="space-between" align="center">
                    <Text size="sm" fw={500}>Invoice</Text>
                    {tripInfo.invoiceurl ? (
                      <Group gap="sm">
                        <ActionIcon
                          variant="light"
                          color="gray"
                          onClick={() => {
                            setSelectedTrip(tripInfo);
                            imageModalHandlers.open();
                          }}
                        >
                          <Avatar
                            src={tripInfo.invoiceurl}
                            size="md"
                            radius="sm"
                          />
                        </ActionIcon>
                        {jobInfo.assigned_to === userId && <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => {
                            setSelectedTripForUpdate(tripInfo);
                            updateInvoiceModalHandlers.open();
                          }}
                        >
                          <IconCamera size={16} />
                        </ActionIcon>}
                      </Group>
                    ) : (
                      <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        leftSection={<IconCamera size={14} />}
                        onClick={() => {
                          setSelectedTripForUpdate(tripInfo);
                          updateInvoiceModalHandlers.open();
                        }}
                      >
                        Add Invoice
                      </Button>
                    )}
                  </Group>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>}

        {/* Update Invoice Modal */}
        <Modal
          opened={updateInvoiceModalOpened}
          onClose={() => {
            updateInvoiceModalHandlers.close();
            setSelectedTripForUpdate(null);
          }}
          title="Update Invoice"
          centered
          size="md"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Take a new photo to update the invoice for this completed trip.
            </Text>
            <CameraAction
              showTitle={false}
              onCapture={handleUpdateInvoice}
              uploadingImage={updatingInvoice}
            />
            {updatingInvoice && (
              <Text size="sm" ta="center" c="blue">
                Updating invoice...
              </Text>
            )}
          </Stack>
        </Modal>
      </Container>
    );
  }
);